// Grant Validation — PSD §9.6 (F9.6). Results screen accordion.

import { useState } from 'react';
import type { HeadlineOutputs } from '@backsolve/shared-types';
import { validateGrants, type GrantRecord } from '@backsolve/shared-logic/grantValidation';

interface GrantValidationProps {
  headline: HeadlineOutputs;
  valuationDate: string;
}

export function GrantValidation({ headline, valuationDate }: GrantValidationProps) {
  const [grants, setGrants] = useState<GrantRecord[]>([]);
  const [holder, setHolder] = useState('');
  const [strike, setStrike] = useState('');
  const [shares, setShares] = useState('');
  const [date, setDate] = useState('');

  const { issues, flaggedGrants } = validateGrants(grants, headline, valuationDate);

  const addGrant = () => {
    if (!holder || !strike || !shares) return;
    setGrants([
      ...grants,
      {
        id: `grant_${Date.now()}`,
        holder,
        strikePrice: parseFloat(strike),
        shares: parseInt(shares, 10),
        grantDate: date || valuationDate,
      },
    ]);
    setHolder('');
    setStrike('');
    setShares('');
    setDate('');
  };

  return (
    <div>
      <p className="result-caption">
        409A requires option strikes to be at or above the FMV measured at grant.
        Add grants below to confirm each strike clears the floor; &ldquo;Below
        FMV&rdquo; grants are flagged with BELOW_FMV_GRANT.
      </p>
      <p>
        FMV floor: <strong>${headline.recommendedStrikePrice.toFixed(2)}/share</strong>
      </p>

      <div className="form-row">
        <input
          type="text"
          placeholder="Holder name"
          value={holder}
          onChange={(e) => setHolder(e.target.value)}
          aria-label="Holder name"
        />
        <input
          type="number"
          step="0.01"
          placeholder="Strike $"
          value={strike}
          onChange={(e) => setStrike(e.target.value)}
          aria-label="Strike price"
        />
        <input
          type="number"
          placeholder="Shares"
          value={shares}
          onChange={(e) => setShares(e.target.value)}
          aria-label="Shares"
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Grant date"
        />
        <button className="btn btn-secondary" onClick={addGrant}>
          Add
        </button>
      </div>

      {grants.length > 0 && (
        <table className="data-table mt-3">
          <thead>
            <tr>
              <th>Holder</th>
              <th>Strike</th>
              <th>Shares</th>
              <th>Date</th>
              <th>Status</th>
              <th>$ to Floor</th>
              <th>Aggregate $</th>
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => {
              const flagged = flaggedGrants.some((f) => f.grant.id === g.id);
              const toFloor = Math.max(0, headline.recommendedStrikePrice - g.strikePrice);
              const aggregate = toFloor * g.shares;
              return (
                <tr key={g.id}>
                  <td>{g.holder}</td>
                  <td>${g.strikePrice.toFixed(2)}</td>
                  <td>{g.shares.toLocaleString()}</td>
                  <td>{g.grantDate}</td>
                  <td>
                    {flagged ? (
                      <span className="badge badge-error">Below FMV</span>
                    ) : (
                      <span className="badge badge-success">OK</span>
                    )}
                  </td>
                  <td>${toFloor.toFixed(2)}</td>
                  <td>${aggregate.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {grants.length > 0 && (
        (() => {
          const affected = flaggedGrants;
          const totalShares = affected.reduce((s, f) => s + f.grant.shares, 0);
          const totalAggregate = affected.reduce(
            (s, f) => s + Math.max(0, headline.recommendedStrikePrice - f.grant.strikePrice) * f.grant.shares,
            0,
          );
          return (
            <p className="field-hint mt-3">
              Flagged grants: <strong>{affected.length}</strong> ·
              Affected shares: <strong>{totalShares.toLocaleString()}</strong> ·
              Aggregate re-pricing exposure: <strong>${totalAggregate.toFixed(2)}</strong>
            </p>
          );
        })()
      )}

      {issues.length > 0 && (
        <ul className="mt-3">
          {issues.map((iss, i) => (
            <li key={i}>
              <span className={`badge badge-${iss.severity === 'error' ? 'error' : 'warning'}`}>
                {iss.code}
              </span>{' '}
              {iss.message}
            </li>
          ))}
        </ul>
      )}

      {grants.length === 0 && <p>No grants entered yet.</p>}
    </div>
  );
}
